/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Settings (strategy 9.8; contract section 11): the service's pulseSettingsSchema with plain labels, edited in
 * one form and saved as only what changed (the StarScribe pattern, lib/pulse.ts); the lock mode as three radio
 * sentences; the health thresholds; the models and the daily budget; the digest times; The Lab filing switch; the jobs'
 * last runs; and Erase all Pulse text now behind a confirmation. Auditors see everything, read-only.
 */
import { Fragment, useEffect, useState } from 'react';
import { Eraser } from 'lucide-react';
import { api } from '../../lib/api';
import {
  LOCK_MODE_OPTIONS, MODEL_CHOICES, PULSE_JOBS, PULSE_NUMBER_FIELDS, WEEKDAY_OPTIONS, pulseForm, pulsePatch, pulseReadFailure, validatePulse, whenText,
  type PulseLockMode, type PulseSettingsForm,
} from '../../lib/pulse';
import { SaveNote } from '../../components/SaveNote';
import { Button, Callout, ConfirmDialog, Field, Grid, Input, NumberInput, PageHeader, Panel, SaveBar, Select, Skeleton, Stack, StatusChip, Switch, Textarea } from '../../ui';
import { count } from '../../ui/format';
import { PulseError, useLoad } from './common';

export function PulseSettings({ canEdit }: { canEdit: boolean }) {
  const { load, reload, reloading, setData } = useLoad(() => api.pulseSettings(), 'settings');
  const view = load.state === 'ready' ? load.data : null;
  const [form, setForm] = useState<PulseSettingsForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [erasing, setErasing] = useState<'ask' | 'busy' | null>(null);
  const [eraseError, setEraseError] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    if (view) setForm(pulseForm(view.settings));
  }, [view]);

  const patch = view && form ? pulsePatch(form, view.settings) : {};
  const changes = Object.keys(patch).length;
  const set = <K extends keyof PulseSettingsForm>(key: K, value: PulseSettingsForm[K]) => {
    setSaved(null);
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  const save = async () => {
    if (!view || !form) return;
    const problem = validatePulse(form);
    if (problem) {
      setSaveError(problem);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const next = await api.savePulseSettings(patch);
      setData(() => next);
      setSaved(`Pulse settings saved: ${Object.keys(patch).join(', ')}. The change is in the audit trail.`);
    } catch (error) {
      setSaveError(pulseReadFailure(error, 'The settings could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const erase = async () => {
    setErasing('busy');
    setEraseError(null);
    try {
      const result = await api.erasePulseText();
      setNote({ ok: true, text: `Erased the question, answer and comment text on ${count(result.erased)} records. Counts and outcomes are kept. The erase is in the audit trail.` });
      setErasing(null);
    } catch (error) {
      setEraseError(pulseReadFailure(error, 'Nothing was erased.'));
      setErasing('ask');
    }
  };

  const number = (key: (typeof PULSE_NUMBER_FIELDS)[number]['key']) => {
    const field = PULSE_NUMBER_FIELDS.find((item) => item.key === key)!;
    return (
      <Field key={key} label={field.label} help={field.hint}>
        <NumberInput aria-label={field.label} value={form?.[key] ?? ''} disabled={!canEdit || !form} onChange={(event) => set(key, event.target.value)} />
      </Field>
    );
  };

  return (
    <>
      <PageHeader meta={<span>How Pulse records, judges, warns and reports. Only IT admins change these; every change is audited.</span>} />
      <div className="ui-page">
        <Stack>
          {load.state === 'loading' ? <Panel title="Loading Pulse settings"><div aria-busy="true"><Skeleton size="lg" /><Skeleton /><Skeleton /></div></Panel> : null}
          {load.state === 'failed' ? <PulseError title="The Pulse settings could not be read" error={load.error} onRetry={reload} retrying={reloading} /> : null}
          {view && form ? (
            <>
              {!canEdit ? <Callout tone="info">Read-only: auditors see the settings; only IT admins change them.</Callout> : null}
              <Panel title="Pulse and feedback cards" description="The switches live under Feature flags; the card's reach is set here">
                <div className="ui-form">
                  <p className="ui-para" aria-label="Pulse status">
                    <StatusChip tone={view.flags.pulse ? 'ok' : 'neutral'}>{view.flags.pulse ? 'Pulse is on' : 'Pulse is off'}</StatusChip>{' '}
                    <StatusChip tone={view.flags.cards ? 'ok' : 'neutral'}>{view.flags.cards ? 'Feedback cards are on' : 'Feedback cards are off'}</StatusChip>{' '}
                    <span className="ui-quiet">{view.flags.cards ? 'The card appears after a higher-profile action settles, up to the number below per PC per day.' : 'Turn on the pulse-cards flag under Feature flags to show the card and the comment box behind thumbs.'}</span>
                  </p>
                  <Grid cols={2}>
                    {number('cardsPerDevicePerDay')}
                    <Field label="Tools whose settled action shows a card" help="One per line, in addition to the built-in list (MCP calls, PPP runs, Word edits, mail, StarScribe edits, connected-app writes).">
                      <Textarea aria-label="Tools whose settled action shows a card" rows={3} value={form.highProfileTools} disabled={!canEdit} onChange={(event) => set('highProfileTools', event.target.value)} />
                    </Field>
                  </Grid>
                </div>
              </Panel>

              <Panel title="What is kept" description="Counts are kept for good; text has a life">
                <div className="ui-form">
                  <Grid cols={2}>
                    {number('retentionDays')}
                    <Field label="Employee id pattern" help="A regular expression for the company's employee ids; matches in questions, answers and comments are replaced with [id]. Leave empty for none.">
                      <Input aria-label="Employee id pattern" value={form.employeeIdPattern} disabled={!canEdit} onChange={(event) => set('employeeIdPattern', event.target.value)} placeholder="^E\\d{5}$" />
                    </Field>
                  </Grid>
                  <Switch label="Keep the answer text (the first 2,000 characters, scrubbed)" checked={form.keepAnswerText} disabled={!canEdit} onChange={(value) => set('keepAnswerText', value)} stateLabels={['Kept', 'Not kept']} />
                </div>
              </Panel>

              <Panel title="Lock mode" description="What Pulse does while a tool is failing (the owner, D5: Warn, and nothing else)">
                <div className="ui-radio-list" role="radiogroup" aria-label="Lock mode">
                  {LOCK_MODE_OPTIONS.map((option) => (
                    <label key={option.value} className="ui-radio">
                      <input type="radio" name="pulse-lock-mode" value={option.value} checked={form.lockMode === option.value} disabled onChange={() => set('lockMode', option.value as PulseLockMode)} />
                      <span className="ui-radio-text">
                        <span className="ui-radio-title">{option.label}</span>
                        <span className="ui-radio-desc pulse-radio-sentence">{option.sentence}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="ui-form">
                  <Switch label="Refuse a second call to a failing tool inside one answer" checked={form.refuseSecondCall} disabled={!canEdit} onChange={(value) => set('refuseSecondCall', value)} stateLabels={['Refused', 'Allowed']} />
                </div>
                <p className="ui-para ui-quiet">Fixed: the quarantine modes were removed. A failing tool stays available; Gyro warns and offers the way round, and only an admin changes anything about a tool. Pulse's own refusals never count as failures, Mute stops them, and Release on Tools &amp; connectors ends a failing reading at once.</p>
              </Panel>

              <Panel title="Health thresholds" description="When a tool reads degraded or failing (Tools & connectors shows the rules as sentences)">
                <Grid cols={2}>
                  {number('degradedPct')}
                  {number('failingPct')}
                  {number('failingMinCalls')}
                  {number('consecutive')}
                  {number('latencyX')}
                </Grid>
              </Panel>

              <Panel title="Judge and analyst" description="The models, the budget, the digest times and when a problem is opened">
                <div className="ui-form">
                  <Grid cols={2}>
                    <Field label="Judge model" help="One call per ask, on the cheap model (the owner, D7).">
                      <Select aria-label="Judge model" value={form.judgeModel} disabled={!canEdit} onChange={(event) => set('judgeModel', event.target.value as 'cheap' | 'strong')}>
                        {MODEL_CHOICES.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                      </Select>
                    </Field>
                    <Field label="Digest model" help="The daily and weekly digests, on the strong model (the owner, D7).">
                      <Select aria-label="Digest model" value={form.digestModel} disabled={!canEdit} onChange={(event) => set('digestModel', event.target.value as 'cheap' | 'strong')}>
                        {MODEL_CHOICES.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                      </Select>
                    </Field>
                    {number('judgeFloor')}
                    {number('dailyBudgetUsd')}
                    <Field label="Daily digest time" help="24-hour HH:MM in the time zone below; the weekly digest runs at the same time on its day.">
                      <Input aria-label="Daily digest time" value={form.digestDailyAt} disabled={!canEdit} onChange={(event) => set('digestDailyAt', event.target.value)} placeholder="05:00" />
                    </Field>
                    <Field label="Weekly digest day">
                      <Select aria-label="Weekly digest day" value={form.digestWeeklyDay} disabled={!canEdit} onChange={(event) => set('digestWeeklyDay', event.target.value)}>
                        {WEEKDAY_OPTIONS.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}
                      </Select>
                    </Field>
                    <Field label="Time zone" help="An IANA zone, such as America/Los_Angeles.">
                      <Input aria-label="Time zone" value={form.timezone} disabled={!canEdit} onChange={(event) => set('timezone', event.target.value)} />
                    </Field>
                    {number('problemMinAsks')}
                    {number('problemMinFailPct')}
                  </Grid>
                </div>
              </Panel>

              <Panel title="The Lab and developers" description="Where a problem file goes beyond Pulse">
                <div className="ui-form">
                  <Switch label="File problems in The Lab when an admin changes their status (the owner, D9)" checked={form.labEnabled} disabled={!canEdit} onChange={(value) => set('labEnabled', value)} stateLabels={['On', 'Off']} />
                  <p className="ui-para ui-quiet">Off by default. Pulse files to The Lab only through The Bridge, and The Bridge has no route for a service-filed Lab report yet: with this on, every filing is refused and recorded on the problem file (lab error) until that route exists. A problem that opens on its own is never filed by itself; filing waits for an admin&apos;s status change.</p>
                  <Field label="The Lab app" help="The app the reports are filed under, until The Lab has its Gyro Assistant page.">
                    <Input aria-label="The Lab app" value={form.labApp} disabled={!canEdit} onChange={(event) => set('labApp', event.target.value)} />
                  </Field>
                  <Switch label="Tell the developers when a tool starts failing" checked={form.notifyDevelopers} disabled={!canEdit} onChange={(value) => set('notifyDevelopers', value)} stateLabels={['On', 'Off']} />
                  <p className="ui-para ui-quiet">No transport exists yet: the only path is The Bridge to Star Command (the-bridge#894). Until that route is live, Pulse records a skipped notification per transition and sends nothing.</p>
                </div>
              </Panel>

              <Panel title="Jobs" description="The daily and weekly digests, the retention sweep and the fix verification, with their last runs">
                <dl className="pulse-jobs" aria-label="Jobs">
                  {PULSE_JOBS.map((job) => {
                    const run = view.jobs?.[job.key];
                    return (
                      <Fragment key={job.key}>
                        <dt>{job.label}</dt>
                        <dd>{!run ? <span className="ui-quiet">not reported yet</span> : run.lastRunAt ? <span>last ran {whenText(run.lastRunAt)} · <StatusChip tone={run.result === 'ok' ? 'ok' : run.result === 'failed' ? 'bad' : 'neutral'}>{run.result ?? 'unknown'}</StatusChip>{run.note ? <span className="ui-quiet"> · {run.note}</span> : null}{run.nextRunAt ? <span className="ui-quiet"> · next {whenText(run.nextRunAt)}</span> : null}</span> : <span className="ui-quiet">has not run yet{run.nextRunAt ? ` · next ${whenText(run.nextRunAt)}` : ''}</span>}</dd>
                      </Fragment>
                    );
                  })}
                </dl>
              </Panel>

              <Panel title="Erase all Pulse text now" description="Drops every stored question, answer, comment, digest, problem note and intent text while keeping the counts and outcomes" footer={<span>The nightly sweep erases text older than the retention above on its own (records, digests, closed problem files, error texts and intents); this erases all of it at once. Audited.</span>}>
                <div className="ui-form">
                  {canEdit ? <div className="ui-actions"><Button variant="danger" icon={Eraser} onClick={() => { setEraseError(null); setErasing('ask'); }}>Erase all Pulse text now</Button></div> : <p className="ui-para ui-quiet">An admin action.</p>}
                  <SaveNote note={note} />
                </div>
              </Panel>

              {canEdit ? (
                <SaveBar
                  changes={changes}
                  saving={saving}
                  error={saveError}
                  saved={saved}
                  saveLabel="Save Pulse settings"
                  onSave={() => void save()}
                  onDiscard={() => { setForm(pulseForm(view.settings)); setSaveError(null); }}
                />
              ) : null}
            </>
          ) : null}
        </Stack>
      </div>
      <ConfirmDialog
        open={erasing !== null}
        title="Erase all Pulse text now?"
        confirmLabel="Erase all text"
        busy={erasing === 'busy'}
        error={eraseError}
        onConfirm={() => void erase()}
        onCancel={() => setErasing(null)}
      >
        Every stored question, answer, comment, failure bundle, digest text, problem note and intent text is erased now. Counts, outcomes, tools, health states and problem files are kept, so the numbers stay; the words go. This cannot be undone.
      </ConfirmDialog>
    </>
  );
}
